import * as R from 'ramda';

import ImmutableMap from '../../language/ImmutableMap';
import { EntitlementHotlist } from '../entitlements/models';
import { GuestDetails } from '../guestDetails';
import { Resort } from '../../resortDetails/resort';
import { getActiveHotlistsForGuest, getHotlistResort } from '../hotlist/module';
import { HistoricalMedia } from '../entitlements/media/mediaHistory/historicalMedia';
import { isActiveHistoricalMedia } from '../entitlements/media/mediaHistory/module';
import { getResource } from '../../language/resources/getResource';


/**
 * Given a guest, a collection of hotlist entitlements, and a collection of resorts, returns a banner message which
 * characterizes the current guest's hotlist status.
 *
 * If the guest is globally hotlisted, the returned message will enunciate this.
 *
 * If the guest is not globally hotlisted but hotlisted at a resort, the returned message will enunciate this.
 *
 * If the guest does not exist or none of the given hotlist entries cannot be attributed to current entitlements held by
 * the guest, we return undefined.
 *
 * @param guest The hotlisted guest
 * @param entitlementHotlists A collection of hotlists, some or all of which should apply to the guest's current
 * entitlements
 * @param resorts A collection of resort details (ideally, all possible resorts)
 */
export const getHotlistBannerTextForGuest = (
    resources: Record<string, any>, 
    guest: GuestDetails | undefined,
    entitlementHotlists: ImmutableMap<EntitlementHotlist[]>,
    resorts: ImmutableMap<Resort>
): string | undefined => {
  // I think I am => I think => ...?
  if (!guest) {
    return undefined;
  }

  const activeHotlistsForGuest = getActiveHotlistsForGuest(guest, entitlementHotlists);

  // Are we sure we're hotlisted?
  if (activeHotlistsForGuest.length === 0) {
    return undefined;
  }

  // Are we globally hotlisted?
  if (R.any(hotlist => hotlist.isHotlistGlobal === true, activeHotlistsForGuest)) {
    return getResource(resources, 'GUEST_CARD_HOTLIST_ALL_RESORTS');
  }

  // Then we are locally hotlisted.
  const hotlistResort = getHotlistResort(R.head(activeHotlistsForGuest)!, resorts);
  return hotlistResort ? getResource(resources, 'GUEST_CARD_HOTLIST_RESORT', hotlistResort.resortName) : getResource(resources, 'GUEST_CARD_HOTLIST');
};

export const getDisabledMediaBannerTextForGuest = (
  resources: Record<string, any>,
  guest: GuestDetails | undefined,
  mediaHistory: HistoricalMedia[] | undefined,
) => {

  if(!guest || !mediaHistory) {
    return undefined;
  }

  const findMedia = (media: HistoricalMedia) =>
    media.guestId === guest.masterGuestId && isActiveHistoricalMedia(media) && media.dtlDisabled;

  return mediaHistory.find(findMedia) ? getResource(resources, 'GUEST_CARD_MEDIA_DISABLED') : undefined;
}

// When text breaks over multiple lines the container component
// extra whitespace that can not be removed using CSS.
// Use this method to remove extra whitespace. 
export function shrinkWrapElement(element) {
  const { firstChild, lastChild } = element;
  if (!element || !firstChild || !lastChild) return;
  const range = document.createRange();
  range.setStartBefore(firstChild);
  range.setEndAfter(lastChild);
  const { width } = range.getBoundingClientRect();
  element.style.width = width + "px";
  element.style.boxSizing = "content-box";
}
